'use client'

import { useTranslations } from 'next-intl'
import { type ReactNode, useEffect, useMemo } from 'react'
import { getActiveAppNavItem } from '@/components/app-nav'
import { useSetPageTitle } from '@/contexts/page-title-context'
import { PAGE_TITLE_CLASSES } from '@/lib/shared/layout-constants'
import { DetailPageBreadcrumb } from './detail-page-breadcrumb'

export type DetailPageHeaderProps = {
  /**
   * Label for the parent collection, such as API or Automations. Omitted,
   * it is the name of the sidebar item `parentHref` belongs to (Settings,
   * Domains), in the reader's language: a server page's static shell can
   * name its parent without reading the locale.
   */
  parentLabel?: string
  /** Route back to the parent collection. */
  parentHref: string
  /**
   * Crumbs between the collection and the current page, for a page nested
   * under an entity (Domains / mail.acme.com / Unsubscribes). Pass a stable
   * (memoized) array: a new one each render re-publishes the title.
   */
  trail?: ReadonlyArray<{ label: string; href: string }>
  /**
   * Current entity label. Null while it loads: the header holds the parent's
   * name, as the parent page reads, and hides `actions`. A loading shell can
   * mount this with `title={null}` so the header never goes blank. A page
   * with a `trail` knows its own title up front, so it never passes null.
   */
  title: string | null
  /**
   * Replaces the static current-page crumb, for a title edited in place (a
   * group's click-to-rename name). It renders its own `aria-current="page"`
   * element in `PAGE_TITLE_CLASSES`. Pass a stable (memoized) element, like
   * `trail`.
   */
  titleSlot?: ReactNode
  /** Optional entity-level action rendered before the global header actions. */
  actions?: ReactNode
}

/**
 * Shared app-header foundation for every page nested under a section
 * (collection → entity, or section → sub-page): `Parent / … / Child`, the
 * parent a plain link back. It owns the breadcrumb structure, canonical title
 * styling, and PageTitleContext lifecycle so nested pages never duplicate
 * navigation chrome (a back arrow, an in-body breadcrumb) inside their body.
 */
export function DetailPageHeader({
  parentLabel,
  parentHref,
  trail,
  title,
  titleSlot,
  actions = null,
}: DetailPageHeaderProps) {
  const t = useTranslations('appShell')
  const setPageTitle = useSetPageTitle()
  const navItem =
    parentLabel === undefined ? getActiveAppNavItem(parentHref) : null
  const resolvedParentLabel =
    parentLabel ?? (navItem ? t(`nav.${navItem.labelKey}`) : '')

  const titleContent = useMemo(() => {
    if (title === null) {
      return <h1 className={PAGE_TITLE_CLASSES}>{resolvedParentLabel}</h1>
    }

    return (
      <DetailPageBreadcrumb
        parentLabel={resolvedParentLabel}
        parentHref={parentHref}
        trail={trail}
        title={title}
        titleSlot={titleSlot}
      />
    )
  }, [parentHref, resolvedParentLabel, title, titleSlot, trail])

  useEffect(() => {
    setPageTitle({ titleContent, rightContent: title ? actions : null })
    return () => setPageTitle({ titleContent: null, rightContent: null })
  }, [actions, setPageTitle, title, titleContent])

  return null
}
